/**
 * Shared parser for content that supports both HTML and Markdown-style syntax.
 * Used for terms and conditions and announcements (rendered with v-html).
 *
 * HTML tags work directly: <b>, <strong>, <i>, <em>, <u>, <a>, <ul>, <br>, etc.
 * Markdown-style: **bold**, *italic*, [text](url), - lists, 1. lists, bare URLs, and \n → paragraphs
 */

function linkifyUrls(text) {
  return text.replace(
    /(^|[\s(>])((https?:\/\/)[^\s<>"')\]]+)/g,
    (match, prefix, url) =>
      `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
}

function parseInlineMarkdown(text) {
  if (!text) return "";

  let result = text;

  result = result.replace(/\*\*\*([^*]+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  result = result.replace(/___([^_]+?)___/g, "<strong><em>$1</em></strong>");
  result = result.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/__([^_]+?)__/g, "<strong>$1</strong>");
  result = result.replace(/(^|[^*])\*([^*]+?)\*([^*]|$)/g, "$1<em>$2</em>$3");
  result = result.replace(/(^|[^_])_([^_]+?)_([^_]|$)/g, "$1<em>$2</em>$3");
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  result = linkifyUrls(result);

  return result;
}

function parseBlockMarkdown(text) {
  const lines = text.split(/\r?\n/);
  const parts = [];
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (!listItems.length) return;
    const tag = listType === "ol" ? "ol" : "ul";
    parts.push(
      `<${tag}>${listItems
        .map((item) => `<li>${parseInlineMarkdown(item)}</li>`)
        .join("")}</${tag}>`
    );
    listItems = [];
    listType = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    const ulMatch = trimmed.match(/^[-*+]\s+(.+)/);
    const olMatch = trimmed.match(/^\d+\.\s+(.+)/);

    if (ulMatch) {
      if (listType === "ol") flushList();
      listType = "ul";
      listItems.push(ulMatch[1]);
      continue;
    }

    if (olMatch) {
      if (listType === "ul") flushList();
      listType = "ol";
      listItems.push(olMatch[1]);
      continue;
    }

    flushList();

    if (!trimmed) {
      continue;
    }

    parts.push(`<p>${parseInlineMarkdown(trimmed)}</p>`);
  }

  flushList();
  return parts.join("");
}

function containsHtmlMarkup(text) {
  return /<(ul|ol|li|p|div|br|a|h[1-6]|strong|em|b|i|u)\b/i.test(text);
}

export function parseMarkdown(text) {
  if (!text) return "";

  const raw = String(text);

  if (containsHtmlMarkup(raw)) {
    return parseInlineMarkdown(raw).replace(/\n/g, "<br>");
  }

  return parseBlockMarkdown(raw);
}

export function getTermsItemText(item) {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  return String(
    item.condition ??
      item.content ??
      item.text ??
      item.description ??
      item.summary ??
      ""
  ).trim();
}

export function normalizeTermsList(terms) {
  if (!terms) return [];
  if (typeof terms === "string") {
    const trimmed = terms.trim();
    return trimmed ? [{ condition: trimmed }] : [];
  }
  if (!Array.isArray(terms)) return [];

  return terms
    .map((item) => {
      if (typeof item === "string") {
        const trimmed = item.trim();
        return trimmed ? { condition: trimmed } : null;
      }
      const text = getTermsItemText(item);
      return text ? { ...item, condition: text } : null;
    })
    .filter(Boolean);
}
